import App from "./App.vue";
import * as Sentry from "@sentry/vue";
import { createApp } from "vue";
import { registerPlugins } from "@/app/plugins";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useAuthStore } from "@/auth/stores/auth";
import i18n from "@/app/plugins/i18n";
import {
  stringDateToStringWithNoTime,
  getTimeFormattedWithAMAndPM,
  stringDateToStringWithDefaultFormat,
  getTimeOnly,
} from "@/shared/utils/date.utils";
import { formatMoney, truncateString } from "@/shared/services/util.service";
import type { FormatMoneyOptions } from "@/shared/services/util.service";
import { hasRoleAccessToRoute } from "@/shared/utils/role.utils";
import { hasFeatureAccess, initFeatureAccess } from "@/shared/utils/feature.utils";
import { registerPwa } from "@/app/pwa/register";
import { initializePwaInstall } from "@/shared/composables/usePwaInstall";
import { initSentry } from "@/app/plugins/sentry";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { isFirebaseAuthNetworkRequestFailed } from "@/shared/utils/firebaseAuthError.utils";
import type { ProductlaneWidget } from "@/shared/types/global";

initializePwaInstall();
registerPwa();

let app: ReturnType<typeof createApp> | null = null;

interface ClickOutsideElement extends HTMLElement {
  __vueClickEventHandler__: (event: MouseEvent) => void;
}

const syncProductlaneUser = (email?: string | null): void => {
  const productlane = (window as Window & { Productlane?: ProductlaneWidget }).Productlane;
  const trimmedEmail = email?.trim();

  if (trimmedEmail) {
    productlane?.set?.({
      user: {
        email: trimmedEmail,
      },
    });
    return;
  }

  productlane?.signOut?.();
};

const auth = getAuth();
onAuthStateChanged(auth, async (user) => {
  syncProductlaneUser(user?.email);
  if (!app) {
    await initFeatureAccess();
    app = createApp(App);
    initSentry(app);
    registerPlugins(app);
    // Custom directives, use ie <div v-date="someDate" />

    app.directive("date", {
      beforeMount: (el: { innerText: string }, binding: { value: number }) => {
        const formattedDate = stringDateToStringWithNoTime(binding.value);
        el.innerText = formattedDate;
      },
    });
    app.directive("dateWithTime", {
      beforeMount: (el: { innerText: string }, binding: { value: number }) => {
        const formattedDate = getTimeFormattedWithAMAndPM(binding.value);
        el.innerText = formattedDate;
      },
    });

    app.directive("timeOnly", {
      beforeMount: (el: { innerText: string }, binding: { value: number }) => {
        const formattedDate = getTimeOnly(binding.value);
        el.innerText = formattedDate;
      },
    });

    app.directive("dateOnly", {
      beforeMount: (el: { innerText: string }, binding: { value: number }) => {
        const formattedDate = stringDateToStringWithDefaultFormat(binding.value);
        el.innerText = formattedDate;
      },
    });

    app.directive("money", {
      beforeMount: (el: HTMLElement, binding: { value: FormatMoneyOptions }) => {
        el.innerText = formatMoney(binding.value);
      },
      updated: (el: HTMLElement, binding: { value: FormatMoneyOptions }) => {
        el.innerText = formatMoney(binding.value);
      },
    });

    app.directive("dash", {
      beforeMount: (el: HTMLElement, binding: { value: string }) => {
        el.innerText = binding.value || "-";
      },
    });

    app.directive("hasRoleAccessToRoute", {
      beforeMount: (el: HTMLElement, binding: { value: { pathName: string } }) => {
        if (!hasRoleAccessToRoute(binding.value.pathName)) {
          el.hidden = true;
        }
      },
    });

    app.directive("feature", {
      beforeMount: (el: HTMLElement, binding: { value: string }) => {
        if (!hasFeatureAccess(binding.value)) {
          el.hidden = true;
        }
      },
      updated: (el: HTMLElement, binding: { value: string }) => {
        if (!hasFeatureAccess(binding.value)) {
          el.hidden = true;
        } else {
          el.hidden = false;
        }
      },
    });

    app.directive("truncate", {
      beforeMount: (el: HTMLElement, binding: { value: [string, number] }) => {
        const [text, length] = binding.value;
        el.innerText = truncateString(text, length);
      },
      updated: (el: HTMLElement, binding: { value: [string, number] }) => {
        const [text, length] = binding.value;
        el.innerText = truncateString(text, length);
      },
    });

    app.directive("click-outside", {
      beforeMount: function (
        el: ClickOutsideElement,
        binding: { value: (event: MouseEvent) => void },
      ) {
        const ourClickEventHandler = (event: MouseEvent) => {
          if (!el.contains(event.target as Node) && el !== event.target) {
            binding.value(event);
          }
        };
        el.__vueClickEventHandler__ = ourClickEventHandler;

        document.addEventListener("click", ourClickEventHandler);
      },
      updated: function (el: ClickOutsideElement) {
        document.removeEventListener("click", el.__vueClickEventHandler__);
      },
    });

    app.mount("#app");

    if (user) {
      useAuthStore().setUserId(user.uid);
      try {
        await useAuthStore().afterLogin();
      } catch (error) {
        if (isFirebaseAuthNetworkRequestFailed(error)) {
          Sentry.addBreadcrumb({
            category: "auth",
            message: "Firebase Auth network request failed during startup login restore",
            level: "warning",
          });
          useSnackBarStore().toastError({
            message: i18n.global.t("errors.networkRequestFailed"),
          });
          return;
        }

        Sentry.captureException(error, {
          tags: {
            area: "auth",
            action: "startupAfterLogin",
          },
        });
      }
    }
  }
});
